import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  Skeleton,
} from '@pantry/ui';
import {
  MoreHorizontalIcon,
  PinIcon,
  PinOffIcon,
  ArchiveIcon,
  ArchiveRestoreIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react';
import type { ShoppingListSummary } from '@pantry/shared';
import { useUpdateShoppingList, useDeleteShoppingList } from '@pantry/api-client';
import { toast } from 'sonner';
import { AnalyticsEvents } from '@pantry/analytics';
import { useAnalytics } from '@/hooks/useAnalytics';
import { EditListDialog } from './EditListDialog';
import { DeleteListDialog } from './DeleteListDialog';
import { UpgradeDialog } from '../premium/UpgradeDialog';

interface ShoppingListCardProps {
  list: ShoppingListSummary;
  householdId: string;
  /** Whether this user has premium access (for pin action) */
  hasPremium: boolean;
}

/**
 * Card component for the shopping lists overview grid.
 * Shows name, progress, pin state, last updated, and overflow menu.
 */
export function ShoppingListCard({ list, householdId, hasPremium }: ShoppingListCardProps) {
  const { t } = useLingui();
  const navigate = useNavigate();

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const updateList = useUpdateShoppingList();
  const deleteList = useDeleteShoppingList();
  const { track } = useAnalytics();

  const progressText =
    list.itemCount === 0 ? t`No items` : t`${list.checkedCount}/${list.itemCount} items`;

  const progressPercent =
    list.itemCount > 0 ? Math.round((list.checkedCount / list.itemCount) * 100) : 0;

  const lastUpdated = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(list.updatedAt));

  const handleCardClick = () => {
    void navigate({ to: '/shopping-lists/$listId', params: { listId: list.id } });
  };

  const handlePin = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasPremium) {
      track(AnalyticsEvents.SHOPPING_LIST_UPGRADE_PROMPTED, { householdId, trigger: 'pin' });
      setShowUpgradeDialog(true);
      return;
    }
    track(AnalyticsEvents.SHOPPING_LIST_PINNED, { householdId, listId: list.id });
    void updateList.mutateAsync({
      householdId,
      listId: list.id,
      data: { isPinned: !list.isPinned },
    });
  };

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Check if list has unchecked items — show confirmation
    const hasUnchecked = list.checkedCount < list.itemCount;
    if (list.isActive && hasUnchecked) {
      setShowArchiveConfirm(true);
    } else {
      void performArchive();
    }
  };

  const performArchive = () => {
    if (list.isActive) {
      track(AnalyticsEvents.SHOPPING_LIST_ARCHIVED, {
        householdId,
        listId: list.id,
        itemCount: list.itemCount,
        checkedCount: list.checkedCount,
      });
    }
    void updateList.mutateAsync({
      householdId,
      listId: list.id,
      data: { isActive: !list.isActive },
    });
  };

  const handleDeleteConfirm = () => {
    track(AnalyticsEvents.SHOPPING_LIST_DELETED, { householdId, listId: list.id });
    void deleteList
      .mutateAsync({ householdId, listId: list.id })
      .then(() => setShowDeleteDialog(false))
      .catch(() => {
        toast.error(t`Failed to delete list. Please try again.`);
      });
  };

  return (
    <>
      <Card
        className="cursor-pointer hover:border-primary/30 hover:shadow-md transition-all duration-200 group"
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        aria-label={t`Open ${list.name}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
      >
        <CardContent className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              {list.isPinned && (
                <PinIcon className="h-3.5 w-3.5 text-primary shrink-0" aria-label={t`Pinned`} />
              )}
              <h3 className="font-medium text-foreground truncate">{list.name}</h3>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={t`List options`}
                >
                  <MoreHorizontalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                  <PencilIcon className="h-4 w-4 mr-2" />
                  <Trans context="action_button">Rename</Trans>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handlePin}>
                  {list.isPinned ? (
                    <>
                      <PinOffIcon className="h-4 w-4 mr-2" />
                      <Trans context="action_button">Unpin</Trans>
                    </>
                  ) : (
                    <>
                      <PinIcon className="h-4 w-4 mr-2" />
                      <Trans context="action_button">Pin</Trans>
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleArchiveClick}>
                  {list.isActive ? (
                    <>
                      <ArchiveIcon className="h-4 w-4 mr-2" />
                      <Trans context="action_button">Archive</Trans>
                    </>
                  ) : (
                    <>
                      <ArchiveRestoreIcon className="h-4 w-4 mr-2" />
                      <Trans context="action_button">Unarchive</Trans>
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteDialog(true);
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2Icon className="h-4 w-4 mr-2" />
                  <Trans context="action_button">Delete</Trans>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Progress */}
          <p className="text-sm text-muted-foreground mb-2">{progressText}</p>
          {list.itemCount > 0 && (
            <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden mb-3">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}

          {/* Footer */}
          <p className="text-xs text-muted-foreground">
            <Trans>Updated {lastUpdated}</Trans>
          </p>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <EditListDialog
        householdId={householdId}
        listId={list.id}
        currentName={list.name}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
      />

      <DeleteListDialog
        open={showDeleteDialog}
        listName={list.name}
        isPending={deleteList.isPending}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Archive confirmation when list has unchecked items */}
      <Dialog
        open={showArchiveConfirm}
        onOpenChange={(isOpen) => !isOpen && setShowArchiveConfirm(false)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Trans context="dialog_title">Archive List?</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>This list has unchecked items. Archive anyway?</Trans>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowArchiveConfirm(false)}>
              <Trans context="action_button">Cancel</Trans>
            </Button>
            <Button
              onClick={() => {
                setShowArchiveConfirm(false);
                performArchive();
              }}
            >
              <Trans context="action_button">Archive</Trans>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <UpgradeDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        feature={t`Pin Shopping Lists`}
        description={t`Pinned lists always appear at the top for quick access.`}
        onUpgrade={() => void navigate({ to: '/upgrade' })}
      />
    </>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

export function ShoppingListCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-7 w-7 rounded" />
        </div>
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-1.5 w-full rounded-full" />
        <Skeleton className="h-3 w-24" />
      </CardContent>
    </Card>
  );
}
